import type { ApiResource } from "../../shared/api/resource.js";

export type PositionReconciliation = "confirmed" | "discrepant" | "pending" | "unavailable";
export type PositionSource = "broker" | "local-projection";

export interface PositionView {
  readonly averagePriceUsd: string;
  readonly brokerAccountId: string;
  readonly currentPriceUsd?: string;
  readonly id: string;
  readonly marketValueUsd?: string;
  readonly observedAt: string;
  readonly priceObservedAt?: string;
  readonly levelsObservedAt?: string;
  readonly levelsSource?: string;
  readonly quantity: string;
  readonly reconciliation: PositionReconciliation;
  readonly side: "flat" | "long" | "short";
  readonly source: PositionSource;
  readonly stale: boolean;
  readonly stopPriceUsd?: string;
  readonly targetPriceUsd?: string;
  readonly symbol: string;
  readonly unrealizedPnlUsd?: string;
}

function record(value: unknown): Record<string, unknown> {
  return value !== null && typeof value === "object" && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : {};
}

function text(value: unknown): string {
  return typeof value === "string" || typeof value === "number" ? String(value) : "";
}

function money(value: unknown): string {
  const object = record(value);
  const minorUnits = text(object.minorUnits);
  if (minorUnits !== "") {
    const parsed = Number(minorUnits);
    return Number.isFinite(parsed) ? String(parsed / 100) : minorUnits;
  }
  return text(value);
}

function reconciliation(value: unknown): PositionReconciliation {
  return ["confirmed", "discrepant", "pending", "unavailable"].includes(String(value))
    ? (value as PositionReconciliation)
    : "unavailable";
}

function source(value: unknown): PositionSource {
  return value === "broker" ? "broker" : "local-projection";
}

export function positionView(resource: ApiResource, now = Date.now()): PositionView {
  const quantity = text(resource.quantity);
  const numericQuantity = Number(quantity);
  const observedAt = text(resource.observedAt);
  const observedTime = Date.parse(observedAt);
  const explicitStale = resource.stale === true || resource.freshness === "stale";
  const currentPriceUsd = money(
    resource.currentPrice ??
      resource.currentPriceUsd ??
      resource.marketPrice ??
      resource.marketPriceUsd,
  );
  const stopPriceUsd = money(resource.stopPrice ?? resource.stopPriceUsd);
  const targetPriceUsd = money(resource.targetPrice ?? resource.targetPriceUsd);
  const marketValueUsd = money(resource.marketValue ?? resource.marketValueUsd);
  const unrealizedPnlUsd = money(resource.unrealizedPnl ?? resource.unrealizedPnlUsd);
  return {
    averagePriceUsd: money(resource.averagePrice),
    brokerAccountId: text(resource.brokerAccountId),
    ...(currentPriceUsd === "" ? {} : { currentPriceUsd }),
    id: resource.id,
    ...(stopPriceUsd === "" ? {} : { stopPriceUsd }),
    ...(targetPriceUsd === "" ? {} : { targetPriceUsd }),
    ...(marketValueUsd === "" ? {} : { marketValueUsd }),
    observedAt,
    ...(text(resource.priceObservedAt) === ""
      ? {}
      : { priceObservedAt: text(resource.priceObservedAt) }),
    ...(text(resource.levelsObservedAt) === ""
      ? {}
      : { levelsObservedAt: text(resource.levelsObservedAt) }),
    ...(text(resource.levelsSource) === "" ? {} : { levelsSource: text(resource.levelsSource) }),
    quantity,
    reconciliation: reconciliation(resource.reconciliation),
    side:
      !Number.isFinite(numericQuantity) || numericQuantity === 0
        ? "flat"
        : numericQuantity > 0
          ? "long"
          : "short",
    source: source(resource.source),
    stale: explicitStale || (Number.isFinite(observedTime) && now - observedTime > 15 * 60 * 1000),
    symbol: text(resource.symbol),
    ...(unrealizedPnlUsd === "" ? {} : { unrealizedPnlUsd }),
  };
}

export function isVisiblePosition(position: PositionView): boolean {
  // Confirmed flat rows are reconciliation history, not open holdings. Keep
  // unresolved rows visible so an operator can still see a broker discrepancy.
  return (
    position.reconciliation !== "confirmed" ||
    position.quantity.trim() === "" ||
    Number(position.quantity) !== 0
  );
}

export function formatUsd(value?: string): string {
  if (value === undefined || value === "") return "Unavailable";
  const parsed = Number(value);
  return Number.isFinite(parsed)
    ? new Intl.NumberFormat(undefined, { currency: "USD", style: "currency" }).format(parsed)
    : value;
}
